import {
  Badge,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import {
  CompanyDraftStatus,
  CompanyStatus,
  CompanyType,
  useGetCompaniesQuery,
  useGetCompanyQuery,
  useGetPartialCompaniesQuery,
} from '@blockpulse3/graphql/hooks';
import {
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
} from '@blockpulse3/ui/commons';
import { useBadge } from '@blockpulse3/ui/ui-hooks';
import { useUser } from '@blockpulse3/web-client/auth';
import { PlusIcon } from '@heroicons/react/outline';
import { useNavigate, useParams } from 'react-router-dom';
import { SwitcherItem } from './SwitcherItem';
import { useWorkspaceSwitcher } from './provider';
import { useEffect } from 'react';

type Props = Omit<ResponsiveModalProps, 'children' | 'isOpen' | 'onClose'>;

/**
 * SwitcherModal.
 * Select another workspace or create a new one.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SwitcherModal({ ...props }: Props): JSX.Element | null {
  const i18nSwitcherModal = useTranslations('SwitcherModal');
  const i18nNewWorkspaceModal = useTranslations('NewWorkspaceModal');

  const navigate = useNavigate();

  /* ** Current workspace ** */
  const { user } = useUser();
  const { companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  /* ** Get items ** */
  const partialsReq = useGetPartialCompaniesQuery();
  const workspacesReq = useGetCompaniesQuery();
  const partials = partialsReq.data?.partials
    ? [...partialsReq.data.partials].sort((a, b) => a.name.localeCompare(b.name))
    : [];
  const workspaces = workspacesReq.data?.companies
    ? [...workspacesReq.data.companies].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  /* ** Get workspace switcher display status and method ** */
  const { isSwitcherOpen, onSwitcherClose } = useWorkspaceSwitcher();

  /* ** Dynamic badge getter ** */
  const { getBadge } = useBadge(
    null,
    [
      {
        value: CompanyDraftStatus.VALIDATED,
        color: 'yellow',
        label: i18nSwitcherModal('DraftSigned'),
      },
      {
        value: CompanyDraftStatus.SIGNED,
        color: 'yellow',
        label: i18nSwitcherModal('DraftSigned'),
      },
      {
        value: CompanyDraftStatus.VERIFIED,
        color: 'yellow',
        label: i18nSwitcherModal('DraftSigned'),
      },
    ],
    { color: 'gray', label: i18nSwitcherModal('DraftTitle') },
  );

  const handlePartialClick = (id: string): void => {
    const partial = partials.find((d) => d.id === id);

    if (!partial) {
      return;
    }

    const companyDraftId = partial?.companyDraft?.id || '';
    const companyDraftStatus = partial?.companyDraft?.status || CompanyDraftStatus.CREATING;

    /* ** Funnel redirection condition ** */
    const isDraftRedirect =
      [CompanyStatus.DRAFT, CompanyStatus.EDITING].includes(partial.status) &&
      [CompanyDraftStatus.EDITING, CompanyDraftStatus.PAYED].includes(companyDraftStatus);

    if (partial.type === CompanyType.SPV && !partial.creationDate) {
      if (isDraftRedirect && companyDraftId) {
        navigate(routes.onboardings.spvEdit.fmt(companyDraftId));
      } else {
        navigate(routes.onboardings.spv.fmt(companyDraftId));
      }
    } else {
      navigate(routes.onboardings.company.infos.fmt(partial.id));
    }
    onSwitcherClose();
  };

  /* ** Redirect to clicked workspace dashboard ** */
  const handleWorkspaceClick = (id: string): void => {
    const selectedCompany = workspaces.find((w) => w.id === id);
    if (selectedCompany) {
      navigate(routes.workspace.fmt(selectedCompany.id));
    }
    onSwitcherClose();
  };

  /* ** Callback on user workspace click ** */
  const handleUserWorkspaceClick = (): void => {
    navigate(routes.home.href);
    onSwitcherClose();
  };

  const handleAddExistingCompany = (): void => {
    navigate(routes.onboardings.company.create.href);
    onSwitcherClose();
  };

  const handleAddSPV = (): void => {
    navigate(routes.onboardings.spvCreate.href);
    onSwitcherClose();
  };

  useEffect(() => {
    if (isSwitcherOpen) {
      workspacesReq.refetch();
      partialsReq.refetch();
    }
  }, [isSwitcherOpen]);

  if (!isSwitcherOpen) return null;

  return (
    <ResponsiveModal
      isCentered
      isOpen={isSwitcherOpen}
      size="xl"
      onClose={onSwitcherClose}
      {...props}
    >
      <ModalOverlay />
      <ModalContent data-cy="switcher-modal">
        <ModalHeader>{i18nSwitcherModal('ModalTitle')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody maxH="500" p="0">
          <Stack spacing="0" w="full">
            {user && (
              <SwitcherItem
                key={user?.id}
                isActive={!companyId}
                isUser={true}
                name={`${user?.individualIdentity?.firstName} ${user?.individualIdentity?.lastName}`}
                profilePicture={user?.individualIdentity?.identity?.profilePicture}
                workspaceId={user?.id}
                onClick={handleUserWorkspaceClick}
              >
                <Text fontSize="sm" fontWeight="light" variant="ellipsis">
                  {i18nSwitcherModal('PersonalSpace')}
                </Text>
              </SwitcherItem>
            )}
            {workspaces.map((curCompany) => (
              <SwitcherItem
                key={curCompany.id}
                isActive={curCompany.id === company?.id}
                isUser={false}
                name={curCompany.name}
                profilePicture={curCompany.identity?.profilePicture}
                workspaceId={curCompany.id}
                onClick={handleWorkspaceClick}
              >
                <Text fontSize="sm" fontWeight="light" variant="ellipsis">
                  {curCompany.registrationNumber}
                </Text>
              </SwitcherItem>
            ))}
            {partials.map((partial) => {
              /* ** Draft based badge if a company_draft is associated to the partial company ** */
              const status = partial?.companyDraft?.status || CompanyDraftStatus.CREATING;
              const badge = getBadge(status);
              return (
                <SwitcherItem
                  key={partial.id}
                  isActive={partial.id === company?.id}
                  isUser={false}
                  profilePicture={partial.identity?.profilePicture}
                  workspaceId={partial.id}
                  name={
                    partial.name ||
                    i18nSwitcherModal('NewCompanyDraft', {
                      id: partial.id.toString(),
                    })
                  }
                  onClick={handlePartialClick}
                >
                  <Badge alignSelf="flex-start" colorScheme={badge.color}>
                    {badge.label}
                  </Badge>
                </SwitcherItem>
              );
            })}
          </Stack>
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter p="4">
          <Stack direction={['column', 'row']} spacing="5" w="full">
            <Button
              data-cy="add-existing-company"
              variant="container"
              onClick={handleAddExistingCompany}
            >
              <HStack alignItems="flex-start">
                <Flex
                  alignItems="center"
                  bg="gray.100"
                  boxSize="32px"
                  flexShrink="0"
                  justifyContent="center"
                  mr="2"
                  rounded="sm"
                >
                  <Icon as={PlusIcon} boxSize="18px" />
                </Flex>
                <Stack>
                  <Text>{i18nNewWorkspaceModal('CompanyExists.Title')}</Text>
                  <Text color="gray.700" fontSize="sm" fontWeight="light">
                    {i18nNewWorkspaceModal('CompanyExists.SubTitle')}
                  </Text>
                </Stack>
              </HStack>
            </Button>
            <Button variant="container" onClick={handleAddSPV}>
              <HStack alignItems="flex-start" data-cy="create-spv-company">
                <Flex
                  alignItems="center"
                  bg="gray.100"
                  boxSize="32px"
                  flexShrink="0"
                  justifyContent="center"
                  mr="2"
                  rounded="sm"
                >
                  <Icon as={PlusIcon} boxSize="18px" />
                </Flex>
                <Stack>
                  <Text>{i18nNewWorkspaceModal('CompanyNotExists.Title')}</Text>
                  <Text color="gray.700" fontSize="sm" fontWeight="light">
                    {i18nNewWorkspaceModal('CompanyNotExists.SubTitle')}
                  </Text>
                </Stack>
              </HStack>
            </Button>
          </Stack>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}

export type SwitcherModalProps = Props;
